import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

/* ------------------------------------------------------------------
   نوارِ باریکِ بالای صفحه هنگامِ رفتن به صفحهٔ دیگر
   ------------------------------------------------------------------
   🔴 ۱۴۰۵/۰۸/۱۳: صفحهٔ قبلی تا رسیدنِ صفحهٔ تازه سرِ جایش می‌ماند (Suspense ِ
   جدای ماژول‌ها برداشته شد — توضیح در index.tsx ِ هر ماژول)؛ به‌جای صفحهٔ
   خالی و چرخنده، این نوار نشان می‌دهد که کلیک گرفته شده و صفحه دارد عوض
   می‌شود.

   · فقط اگر رفتن بیش از ۱۵۰ میلی‌ثانیه طول بکشد پیدا می‌شود — رفتنِ فوری
     هیچ نشانی نمی‌گذارد.
   · آغاز: کلیک روی پیوندِ درونیِ پنل یا دکمهٔ برگشت/جلوی مرورگر. پایان:
     عوض شدنِ نشانی، که React Router درست همان لحظهٔ نشستنِ صفحهٔ تازه
     گزارش می‌کند (هر رفتن در startTransition است).
   · ⚠️ کلیک در فازِ capture خوانده می‌شود و `defaultPrevented` نادیده است:
     Link ِ React Router خودش preventDefault می‌کند.
   · تورِ ایمنی: اگر نشانی عوض نشد (پیوند به همان صفحه، یا رفتنی که
     رد شد)، پس از ۱۰ ثانیه خودش می‌رود.
   ظاهر: ‎.pf-nav-progress‎ در tokens.css.
   ------------------------------------------------------------------ */

const SHOW_AFTER_MS = 150
const GIVE_UP_MS = 10_000
const FADE_MS = 360

type Phase = 'idle' | 'running' | 'done'

export function NavProgress() {
  const location = useLocation()
  const [phase, setPhase] = useState<Phase>('idle')
  const here = useRef('')
  here.current = location.pathname + location.search
  const showTimer = useRef(0)
  const giveUpTimer = useRef(0)

  useEffect(() => {
    const start = () => {
      window.clearTimeout(showTimer.current)
      window.clearTimeout(giveUpTimer.current)
      showTimer.current = window.setTimeout(() => setPhase('running'), SHOW_AFTER_MS)
      giveUpTimer.current = window.setTimeout(() => setPhase('idle'), GIVE_UP_MS)
    }
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return
      const url = new URL(link.href, window.location.href)
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/app')) return
      if (url.pathname + url.search === here.current) return
      start()
    }
    document.addEventListener('click', onClick, true)
    window.addEventListener('popstate', start)
    return () => {
      document.removeEventListener('click', onClick, true)
      window.removeEventListener('popstate', start)
      window.clearTimeout(showTimer.current)
      window.clearTimeout(giveUpTimer.current)
    }
  }, [])

  /* صفحهٔ تازه نشست */
  useEffect(() => {
    window.clearTimeout(showTimer.current)
    window.clearTimeout(giveUpTimer.current)
    setPhase((current) => (current === 'running' ? 'done' : 'idle'))
  }, [location.pathname, location.search])

  useEffect(() => {
    if (phase !== 'done') return
    const timer = window.setTimeout(() => setPhase('idle'), FADE_MS)
    return () => window.clearTimeout(timer)
  }, [phase])

  return <div aria-hidden="true" className="pf-nav-progress" data-phase={phase} />
}
